import React from "react";
import { motion } from "framer-motion";
import { ChevronRight, Zap } from "lucide-react";

import AskToko from "../Photos/Ask_Toko.png";
import puzzle from "../Photos/puzzle.png";
import animalsafari from "../Photos/animalsafari.png";
import story from "../Photos/story.png";
import draw from "../Photos/draw.png";
import games from "../Photos/games.png";
import writing from "../Photos/writing.png";
import previewImg from "../Photos/preview.png";
import { Link } from "react-router-dom";

// --- Reusable Feature Card Component ---
// ICON is replaced by IMAGE_PLACEHOLDER/THUMBNAIL
const FeatureCard = ({
  title,
  description,
  thumbnailUrl,
  colorClass,
  delay,
  path, // Added path prop
}) => (
  <Link to={path} className="block no-underline">
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      whileInView={{ opacity: 1, scale: 1 }}
      whileHover={{ y: -5 }} // Added a slight lift on hover
      transition={{ duration: 0.5, delay: delay, type: "spring", stiffness: 100 }}
      viewport={{ once: true, amount: 0.5 }}
      className="p-4 sm:p-6 bg-white rounded-3xl shadow-xl border-4 border-b-8 hover:shadow-2xl hover:border-b-4 transition-all duration-300 cursor-pointer overflow-hidden h-full"
      style={{ borderColor: colorClass.border, backgroundColor: colorClass.bg }}
    >
      <div className="flex flex-col items-center text-center space-y-3">
        <div
          className="w-full h-32 sm:h-40 rounded-xl mb-3 flex items-center justify-center p-2 border-2 border-gray-100/50"
          style={{ backgroundColor: colorClass.imageBg }}
        >
          <img
            src={thumbnailUrl}
            alt={title}
            className="w-full h-full object-cover rounded-lg"
          />
        </div>

        <h3 className="text-xl font-extrabold text-gray-900 mt-2">{title}</h3>
        <p className="text-xs sm:text-sm text-gray-500 max-w-xs">{description}</p>
      </div>
    </motion.div>
  </Link>
);

// --- Main Component ---
export default function FeaturesGrid() {
  // 🎨 Defined GREEN-FOCUSED color palettes
  const palettes = {
    emerald: { imageBg: "#E6FFEE", border: "#4ADE80", bg: "#F7FFF7" }, // Light Mint/Emerald
    teal: { imageBg: "#CCFBF1", border: "#2DD4BF", bg: "#F0FDFA" }, // Light Cyan/Teal
    lime: { imageBg: "#ECFCCB", border: "#A3E635", bg: "#F7FEE7" }, // Light Lime/Yellow-Green
    blue_accent: { imageBg: "#BFDBFE", border: "#60A5FA", bg: "#EFF6FF" }, // Light Blue Accent
    yellow_accent: { imageBg: "#FDE68A", border: "#FCD34D", bg: "#FFFBEB" }, // Light Yellow Accent
  };

const features = [
    {
      title: "Magic Story Time",
      thumbnailUrl: story, 
      description: "New adaptive stories every day to build vocabulary and imagination.",
      colorClass: palettes.emerald,
      path: "/storytime"
    },
    {
      title: "Ask Toko Anything!",
      thumbnailUrl: AskToko,
      description: "Our friendly AI buddy answers questions and talks about feelings safely.",
      colorClass: palettes.teal,
      path: "/lifeskills"
    },
    {
      title: "Tracing & Writing",
      thumbnailUrl: writing,
      description: "Fun, guided practice for letters and numbers to improve motor skills.",
      colorClass: palettes.lime,
      path: "/alphabet"
    },
    {
      title: "Brainy Games",
      thumbnailUrl: games,
      description: "Puzzles and memory challenges that boost quick thinking and focus.",
      colorClass: palettes.blue_accent,
      path: "/playgames"
    },
    {
      title: "Shape Puzzles",
      thumbnailUrl: puzzle,
      description: "Interactive shape and color sorting to develop early math skills.",
      colorClass: palettes.yellow_accent,
      path: "/puzzlecorner"
    },
    {
      title: "Animal Safari",
      thumbnailUrl: animalsafari,
      description: "Meet friendly animals, hear their sounds, and learn about nature.",
      colorClass: palettes.teal,
      path: "/animals"
    },
    {
      title: "Creative Drawing Pad",
      thumbnailUrl: draw,
      description: "A digital art space for free drawing and coloring story characters.",
      colorClass: palettes.blue_accent,
      path: "/createdraw"
    },
  ];

  return (
    <section className="py-20 px-6 bg-gradient-to-b from-green-100 to-teal-100">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h2 className="text-5xl font-extrabold text-emerald-800 mb-4">
            The World of Fun Learning 
          </h2>
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            A safe, creative, and fully guided space where every tap is a step
            toward confidence and knowledge.
          </p>
        </div>

        {/* Main Devices Image Placeholder - Mimics the image structure */}
<div className="flex justify-center mb-16">
  <img
    src={previewImg}
    alt="Talkytoon App Preview"
    className="w-60 sm:w-80 md:w-96 rounded-2xl shadow-lg object-cover"
  />
</div>


        {/* Features Grid */}
  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
        {features.map((feature, i) => (
          <FeatureCard
            key={i}
            title={feature.title}
            description={feature.description}
            thumbnailUrl={feature.thumbnailUrl}
            colorClass={feature.colorClass}
            path={feature.path}
            delay={i * 0.05}
          />
        ))}
      </div>
        {/* CTA */}
        {/* <div className="mt-16 text-center">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-2 bg-emerald-600 text-white text-xl font-bold px-10 py-4 rounded-full shadow-2xl hover:bg-emerald-700 transition duration-300"
          >
            <Zap className="w-6 h-6" />
            Start Your Free 7-Day Trial
            <ChevronRight className="w-5 h-5 ml-1" />
          </motion.button>
        </div> */}
      </div>
    </section>
  );
}
